import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Factory, Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  SUPPLIER_KIND_LABELS,
  useCreateSupplier,
  useSuppliers,
  type Supplier,
  type SupplierKind,
} from '@/api/sourcing'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuLabel,
  PickerMenuList,
  PickerMenuSearch,
  PickerMenuSeparator,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { cn } from '@/lib/utils'

const CREATABLE_KINDS: SupplierKind[] = ['distributor', 'rep_group', 'online_retailer']

/**
 * Who a manufacturer's products are ordered through: the manufacturer itself
 * (null), a supplier, or nothing chosen yet (undefined). Suppliers can be added
 * from the search box, which opens on ``initialSearch`` when one is given.
 */
export function SupplierPicker({
  id,
  value,
  onChange,
  initialSearch = '',
  disabled = false,
  className,
}: {
  id?: string
  value: number | null | undefined
  onChange: (supplier: Supplier | null) => void
  initialSearch?: string
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(initialSearch)
  const suppliers = useSuppliers()
  const createSupplier = useCreateSupplier()
  const orderable = useMemo(
    () => (suppliers.data ?? []).filter((supplier) => supplier.kind !== 'buying_group'),
    [suppliers.data],
  )
  const query = search.trim()
  const filtered = query
    ? orderable.filter((supplier) => supplier.name.toLowerCase().includes(query.toLowerCase()))
    : orderable
  const exact = orderable.some((supplier) => supplier.name.toLowerCase() === query.toLowerCase())
  const current = orderable.find((supplier) => supplier.id === value)

  const choose = (supplier: Supplier | null) => {
    setOpen(false)
    setSearch(initialSearch)
    onChange(supplier)
  }

  return (
    <PickerMenu
      open={open}
      onOpenChange={(nextOpen) => {
        if (!disabled) setOpen(nextOpen)
        if (!nextOpen) setSearch(initialSearch)
      }}
    >
      <PickerMenuTrigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border bg-background px-3 text-left text-sm shadow-xs transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60',
            className,
          )}
        >
          <span className="min-w-0 flex-1 truncate font-medium">
            {value === null ? 'Direct from manufacturer' : (current?.name ?? 'Choose supplier')}
          </span>
          {current && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {SUPPLIER_KIND_LABELS[current.kind]}
            </span>
          )}
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PickerMenuTrigger>
      <PickerMenuContent title="Order through" align="start" className="w-80">
        <PickerMenuSearch
          value={search}
          onChange={setSearch}
          placeholder="Search or add a supplier"
          ariaLabel="Search suppliers"
        />
        <PickerMenuList className="max-h-72">
          {!query && (
            <PickerMenuItem className="min-h-10 justify-between" onSelect={() => choose(null)}>
              <span className="flex items-center gap-2 font-medium">
                <Factory className="size-4 text-muted-foreground" />
                Direct from manufacturer
              </span>
              {value === null && <Check className="size-4" />}
            </PickerMenuItem>
          )}
          {suppliers.isLoading ? (
            <div className="flex min-h-16 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading suppliers
            </div>
          ) : (
            filtered.map((supplier) => (
              <PickerMenuItem
                key={supplier.id}
                className="min-h-10 justify-between"
                onSelect={() => choose(supplier)}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{supplier.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {SUPPLIER_KIND_LABELS[supplier.kind]}
                  </span>
                </span>
                {supplier.id === value && <Check className="size-4" />}
              </PickerMenuItem>
            ))
          )}
          {query && !exact && (
            <>
              <PickerMenuSeparator />
              <PickerMenuLabel className="px-2 text-xs font-medium text-muted-foreground">
                Add “{query}” as
              </PickerMenuLabel>
              {CREATABLE_KINDS.map((kind) => (
                <PickerMenuItem
                  key={kind}
                  disabled={createSupplier.isPending}
                  onSelect={(event) => {
                    event.preventDefault()
                    createSupplier.mutate(
                      { name: query, kind },
                      {
                        onSuccess: (supplier) => choose(supplier),
                        onError: () => toast.error(`Could not add ${query}`),
                      },
                    )
                  }}
                >
                  <Plus className="size-4 text-muted-foreground" />
                  {SUPPLIER_KIND_LABELS[kind]}
                </PickerMenuItem>
              ))}
            </>
          )}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}
